if (!customElements.get('quick-comparison-table')) {
  customElements.define(
    'quick-comparison-table',
    class QuickComparisonTable extends HTMLElement {
      constructor() {
        super();
        if ('requestIdleCallback' in window) {
          requestIdleCallback(this.init.bind(this), { timeout: 1500 });
        } else {
          FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '0px 0px 400px 0px' });
        }
      }

      init() {
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((responseText) => {
            const doc = new DOMParser().parseFromString(responseText, 'text/html');
            const sectionInnerHTML = doc.querySelector('.shopify-section');
            if (!sectionInnerHTML) return;

            const recommendations = sectionInnerHTML.querySelector('quick-comparison-table');
            if (recommendations && recommendations.innerHTML.trim().length) {
              this.innerHTML = recommendations.innerHTML;
              this.buildSpecsComparison();
              this.initHighlightToggle();
              this.dispatchEvent(new CustomEvent('quick-comparison-table:loaded'));
            } else {
              this.closest('.shopify-section').remove();
              this.dispatchEvent(new CustomEvent('quick-comparison-table:empty'));
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }

      /**
       * Parse specs HTML from hidden <template> elements and build comparison rows.
       */
      buildSpecsComparison() {
        const templates = this.querySelectorAll('.sw-compare__specs-data');
        const currentId = this.dataset.currentProductId;
        const tbody = this.querySelector('.sw-compare__specs-body');
        if (!tbody || templates.length === 0) return;

        // Synonym map: collapses semantically-equivalent labels from
        // different manufacturers/data sources to a single canonical key.
        // Values are the normalized form (lowercase, no punctuation).
        const SYNONYMS = {
          // Body
          'body': 'body material',
          'body wood': 'body material',
          'body woods': 'body material',
          'top wood': 'body material',
          // Nut (material)
          'string nut': 'nut material',
          'nut': 'nut material',
          // Fingerboard / Fretboard
          'fretboard': 'fingerboard material',
          'fretboard material': 'fingerboard material',
          'fingerboard': 'fingerboard material',
          'fretboard radius': 'fingerboard radius',
          // Neck
          'neck wood': 'neck material',
          'neck': 'neck material',
          'neck profile': 'profile',
          'neck shape': 'profile',
          'neck joint': 'joint',
          // Frets (count vs size — keep them DISTINCT: "Fret Count" = number, "Frets" = size)
          'number of frets': 'fret count',
          'no of frets': 'fret count',
          // Pickups / Switching
          'pickups configuration': 'pickup configuration',
          'pickup config': 'pickup configuration',
          'switching': 'pickup selector',
          // Hardware
          'hardware finish': 'finish',
          // Strings
          'number of strings': 'strings',
          'strings gauge': 'strings',
          'string gauge': 'strings',
          // Case / bag
          'case included': 'case',
          'hard case': 'case',
          'hardshell case': 'case',
          'gig bag included': 'gig bag',
          'gigbag': 'gig bag',
          // Locale
          'colour': 'color',
        };

        // Normalizes a spec label for DEDUP comparison: unicode-normalizes,
        // replaces non-breaking/zero-width spaces, strips "Refinement "
        // prefix, removes all punctuation, collapses whitespace, lowercases,
        // then maps through the synonym table. Used as object key so
        // "FINGERBOARD RADIUS" / "Fingerboard Radius" / "Refinement
        // Fingerboard Radius" / "Fretboard Radius" all collapse to one row.
        const normalizeDedupKey = (raw) => {
          if (!raw) return '';
          let k = String(raw)
            .normalize('NFKD')
            .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
          k = k.replace(/^refinement\s+/i, '');
          k = k.replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
          if (SYNONYMS[k]) k = SYNONYMS[k];
          return k;
        };
        const prettifyKey = (raw) => {
          let k = String(raw || '')
            .normalize('NFKD')
            .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/^refinement\s+/i, '')
            .replace(/[:.;]+$/, '')
            .trim();
          const letters = k.replace(/[^A-Za-z]/g, '');
          if (letters.length > 1 && letters === letters.toUpperCase()) {
            k = k.toLowerCase().replace(/\b(\w)/g, (m) => m.toUpperCase());
            k = k.replace(/\b(Of|And|The|Or|For|To|In|On|At|By|With)\b/g, (m) => m.toLowerCase());
            k = k.replace(/^./, (m) => m.toUpperCase());
          }
          return k;
        };
        const normalizeSectionKey = (raw) => {
          if (!raw) return '';
          return String(raw)
            .normalize('NFKD')
            .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase()
            .replace(/[^\w\s]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        };

        // Parse specs for each product
        const productOrder = [];
        const productSpecs = {};

        templates.forEach((tpl) => {
          const pid = tpl.dataset.productId;
          const productTitle = (tpl.dataset.productTitle || '').toLowerCase();
          productOrder.push(pid);

          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = tpl.innerHTML;

          const specs = {};
          const rows = Array.from(tempDiv.querySelectorAll('tr'));

          // Some metafields store a multi-model catalog spec sheet (one
          // table describing multiple siblings side-by-side). The first row
          // lists model names, subsequent rows have:
          //   <td colspan="2">Label</td><td>Value for Model A</td><td>Value for Model B</td>
          // Pick the column that matches THIS product's title, fall back to
          // the first value column.
          let multiModelValueColIdx = -1; // -1 = not a multi-model layout
          if (rows.length > 0) {
            const firstRowCells = Array.from(rows[0].querySelectorAll('td, th')).map((c) =>
              c.textContent.trim()
            );
            // Heuristic: first cell empty (header placeholder) AND 2+ other
            // non-empty cells that look like model codes/names (short text).
            if (
              firstRowCells.length >= 3 &&
              !firstRowCells[0] &&
              firstRowCells.slice(1).filter((x) => x && x.length < 80).length >= 2
            ) {
              // Try to match product title against column headers
              let matched = -1;
              for (let i = 1; i < firstRowCells.length; i++) {
                const header = firstRowCells[i].toLowerCase();
                if (header && productTitle.includes(header)) {
                  matched = i;
                  break;
                }
              }
              multiModelValueColIdx = matched >= 0 ? matched : 1; // fall back to first value column
            }
          }

          let currentSection = '';

          rows.forEach((row, rowIdx) => {
            // Skip the multi-model header row itself
            if (multiModelValueColIdx >= 0 && rowIdx === 0) return;

            const cells = row.querySelectorAll('td, th');
            const cellTexts = Array.from(cells).map((c) => c.textContent.trim());
            const len = cellTexts.length;

            // Multi-model layout: cells are [label, val_A, val_B, ...].
            // Take label=cells[0], value=cells[multiModelValueColIdx].
            if (multiModelValueColIdx >= 0 && len >= 2) {
              const label = cellTexts[0];
              const value = cellTexts[multiModelValueColIdx] || '';
              // Section row in multi-model: first cell filled, others empty
              // (often uses colspan — the DOM shows only 1 TD).
              if (label && !cellTexts.slice(1).some((c) => c)) {
                currentSection = label;
                return;
              }
              if (label && value) {
                const dedupKey = normalizeDedupKey(label);
                if (!specs[dedupKey]) {
                  specs[dedupKey] = {
                    value,
                    section: currentSection,
                    label: prettifyKey(label),
                  };
                }
                return;
              }
              // nothing matched in multi-model row; skip to avoid mis-parsing
              return;
            }

            // Support both 2-column tables (Label|Value) and 3-column tables
            // where the FIRST column carries the section name and the last
            // two carry label|value. Yamaha Revstar and other accentuate-
            // origin products use the 3-column layout:
            //   <tr><td>General</td><td>Construction</td><td>Set-in</td></tr>
            //   <tr><td></td>       <td>Scale Length</td><td>24-3/4"</td></tr>
            if (len >= 2) {
              const label = cellTexts[len - 2];
              const value = cellTexts[len - 1];

              if (label && value) {
                // For 3+ column rows, first cell (when non-empty AND not the
                // same as the label cell) is the section name for this row.
                if (len >= 3 && cellTexts[0] && cellTexts[0] !== label) {
                  currentSection = cellTexts[0];
                }
                const dedupKey = normalizeDedupKey(label);
                // Dedupe: first-seen wins — prevents Refinement/casing/
                // synonym variants from duplicating rows.
                if (!specs[dedupKey]) {
                  specs[dedupKey] = {
                    value,
                    section: currentSection,
                    label: prettifyKey(label),
                  };
                }
                return;
              }

              // Section-title row in a 3-column table (first cell filled,
              // label+value empty): <tr><td>General Specifications</td><td></td><td></td></tr>
              if (cellTexts[0] && !label && !value) {
                currentSection = cellTexts[0];
                return;
              }
            }

            // Otherwise treat as a section header row (single-cell row or an
            // explicit section-header class with h4/h3/strong inside).
            if (
              row.classList.contains('product-specs-row--section') ||
              row.querySelector('h4, h3, h2, strong')
            ) {
              const heading = row.querySelector('h4, h3, h2, strong');
              if (heading) {
                currentSection = heading.textContent.trim();
              } else if (cells.length === 1) {
                currentSection = cells[0].textContent.trim();
              }
            }
          });

          productSpecs[pid] = specs;
        });

        if (productOrder.length === 0) return;

        // Collect all unique spec keys in order, preserving first-seen order
        const allKeys = [];
        const keyToSection = {};
        const seenKeys = new Set();

        productOrder.forEach((pid) => {
          const specs = productSpecs[pid];
          Object.keys(specs).forEach((key) => {
            if (!seenKeys.has(key)) {
              seenKeys.add(key);
              allKeys.push(key);
              keyToSection[key] = specs[key].section;
            }
          });
        });

        // Hide rows where fewer than 2 products have a real value. A row
        // with only one product filled and the rest showing "-" is not a
        // useful comparison — it just bloats the table. If there are 2 or
        // fewer products total, keep all rows.
        const minProductsWithValue = productOrder.length > 2 ? 2 : 1;
        const filteredKeys = allKeys.filter((key) => {
          let count = 0;
          for (let i = 0; i < productOrder.length; i++) {
            if (productSpecs[productOrder[i]][key]) {
              count++;
              if (count >= minProductsWithValue) return true;
            }
          }
          return false;
        });

        if (filteredKeys.length === 0) {
          tbody.innerHTML =
            '<tr><td colspan="99" class="sw-compare__no-specs">No detailed specifications available for comparison.</td></tr>';
          return;
        }

        // Group keys by section. Use a Map keyed on a normalized section
        // name so the same section (e.g. "ACCESSORIES" from product A and
        // "Accessories" from product B) appears only once, preserving the
        // first-seen display name and first-seen position.
        const sectionMap = new Map();
        filteredKeys.forEach((key) => {
          const rawSec = (keyToSection[key] || '').trim();
          const secKey = normalizeSectionKey(rawSec);
          if (!sectionMap.has(secKey)) {
            sectionMap.set(secKey, { name: rawSec, keys: [] });
          }
          sectionMap.get(secKey).keys.push(key);
        });
        // Sections whose normalized name is in BOTTOM_SECTIONS are anchored
        // to the end of the table regardless of where they first appeared.
        // All other sections keep their first-seen order.
        const BOTTOM_SECTIONS = ['accessories'];
        const sections = Array.from(sectionMap.entries())
          .sort((a, b) => {
            const aBottom = BOTTOM_SECTIONS.indexOf(a[0]);
            const bBottom = BOTTOM_SECTIONS.indexOf(b[0]);
            if (aBottom === -1 && bBottom === -1) return 0;
            if (aBottom === -1) return -1;
            if (bBottom === -1) return 1;
            return aBottom - bBottom;
          })
          .map((entry) => entry[1]);

        // Build rows
        const fragment = document.createDocumentFragment();

        sections.forEach((sec) => {
          // Section header row
          if (sec.name) {
            const headerRow = document.createElement('tr');
            headerRow.className = 'sw-compare__row sw-compare__row--section-header';

            const labelCell = document.createElement('td');
            labelCell.className = 'sw-compare__label-cell';
            labelCell.textContent = sec.name;
            headerRow.appendChild(labelCell);

            productOrder.forEach((pid) => {
              const td = document.createElement('td');
              td.className = 'sw-compare__product-cell';
              if (pid === currentId) td.classList.add('sw-compare__product-cell--current');
              headerRow.appendChild(td);
            });

            fragment.appendChild(headerRow);
          }

          // Spec rows
          sec.keys.forEach((key) => {
            const row = document.createElement('tr');
            row.className = 'sw-compare__row';

            const labelCell = document.createElement('td');
            labelCell.className = 'sw-compare__label-cell';
            // Use the first-seen pretty label (not the lowercase dedup key)
            const displayLabel = (function () {
              for (let i = 0; i < productOrder.length; i++) {
                const s = productSpecs[productOrder[i]][key];
                if (s && s.label) return s.label;
              }
              return key;
            })();
            labelCell.textContent = displayLabel;
            row.appendChild(labelCell);

            const values = [];
            productOrder.forEach((pid) => {
              const td = document.createElement('td');
              td.className = 'sw-compare__product-cell';
              if (pid === currentId) td.classList.add('sw-compare__product-cell--current');

              const spec = productSpecs[pid][key];
              const val = spec ? spec.value : '-';
              td.textContent = val;
              values.push(val.toLowerCase().replace(/\s+/g, ' ').trim());

              row.appendChild(td);
            });

            // Check if all values are the same (for highlight differences)
            const uniqueValues = new Set(values);
            row.setAttribute('data-all-same', uniqueValues.size <= 1 ? 'true' : 'false');

            fragment.appendChild(row);
          });
        });

        tbody.appendChild(fragment);
      }

      /**
       * Wire up the "Highlight Differences" checkbox.
       */
      initHighlightToggle() {
        const checkbox = this.querySelector('.sw-compare__highlight-checkbox');
        const table = this.querySelector('.sw-compare__table');
        if (!checkbox || !table) return;

        checkbox.addEventListener('change', () => {
          table.classList.toggle('sw-compare__table--highlight-active', checkbox.checked);
        });
      }
    }
  );
}
